/**
 * ppo-reorder-to-designer.js  –  PPO (Promotional Products Online)
 *
 * When a customer reorders an ImprintNext-designed product via "Buy Again",
 * the item lands in the cart at the ORIGINAL order's baked-in price — wrong
 * for any new quantity. The tiny thumbnail also gives no confidence the art
 * is correct, and there's no way to edit colors, text, or quantity.
 *
 * DETECTION STRATEGY:
 * "Buy Again" strips the _refid line item property, so we can't rely on it.
 * Instead, we detect IN phantom products via:
 *   A) Cart rows with data-in-phantom="true" (set by Liquid via product tags)
 *      — these are "Buy Again" reorders where _refid was lost
 *
 * For case A (phantom without _refid):
 *   1. Read the phantom variant ID from data-phantom-variant
 *   2. Call IN's api/v1/shopify-product/<variantId> to get original product ID
 *   3. Build a designer URL for the original product
 *   4. Show banner directing customer to the designer
 *
 * Works alongside ppo-cart-reorder-guard.js (which handles non-IN tiered products).
 */
(function () {
  'use strict';

  // ── Configuration ──────────────────────────────────────────────────
  var APP_URL = 'https://designer.promotionalproductsonline.com/designer/';
  var POLL_INTERVAL = 300;
  var MAX_POLLS = 40;
  var BANNER_CLASS = 'ppo-reorder-designer-banner';

  // ── Helper: find Mold line-item keys in the cart ──────────────────
  function getMoldLineItemKeys() {
    var keys = [];
    document.querySelectorAll('tr.cart-item').forEach(function (row) {
      var title = (row.getAttribute('data-product-title') || '').toLowerCase();
      if (title.indexOf('mold') >= 0) {
        var k = row.getAttribute('data-line-item-key');
        if (k) keys.push(k);
      }
    });
    return keys;
  }

  function removeLineItems(keys, callback) {
    if (!keys.length) { callback(); return; }
    var key = keys.shift();
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
    .then(function () { removeLineItems(keys, callback); })
    .catch(function () { removeLineItems(keys, callback); });
  }

  // ── Brand Colors ───────────────────────────────────────────────────
  var COLORS = {
    primary:    '#676BA2',   // PPO brand purple
    primaryDk:  '#555994',   // darker shade for hover
    primaryLt:  '#8286B3',   // lighter shade
    bgStart:    '#F0F0F7',   // banner gradient start
    bgEnd:      '#F5F5FA',   // banner gradient end
    border:     '#676BA2',
    text:       '#1d1d1d',
    textMed:    '#444',
    textLight:  '#666',
    textMuted:  '#888',
    white:      '#fff'
  };

  // Only run on cart page
  if (window.location.pathname !== '/cart') return;

  console.log('[ppo-reorder-to-designer] Initializing on cart page');

  // ── Encryption (same as xe_app1.js / IN's cipher) ─────────────────
  function encrypt(urlToEncode, secretKey) {
    var enCodedBit, encryptedTxt = '', cipherKey = '5';
    urlToEncode += secretKey;
    for (var i = 0; i < urlToEncode.length; i++) {
      enCodedBit = String.fromCharCode(urlToEncode[i].charCodeAt(0) + cipherKey.charCodeAt(0));
      encryptedTxt += enCodedBit;
    }
    return btoa(encryptedTxt);
  }

  // ── Fetch IN secret key ────────────────────────────────────────────
  var secretKeyData = '';
  function getSecretKey() {
    return fetch(APP_URL + 'api/v1/secretkey')
      .then(function (r) { return r.text(); })
      .then(function (data) {
        secretKeyData = JSON.parse(data);
        console.log('[ppo-reorder-to-designer] Secret key loaded');
        return secretKeyData;
      });
  }

  // ── Resolve phantom variant → original product ─────────────────────
  function resolvePhantomVariant(phantomVariantId) {
    return fetch(APP_URL + 'api/v1/shopify-product/' + phantomVariantId)
      .then(function (r) { return r.text(); })
      .then(function (data) {
        var ids = JSON.parse(data);
        console.log('[ppo-reorder-to-designer] Resolved phantom variant', phantomVariantId, '→', ids);
        return ids;
      });
  }

  // ── Build discount JSON from tier data ──────────────────────────────
  function buildDiscountJson(row) {
    var tierDataStr = row.getAttribute('data-discount-tiers');
    var variantPrice = parseInt(row.getAttribute('data-variant-price') || '0', 10);
    var sku = row.getAttribute('data-product-sku') || '';

    if (!tierDataStr || !variantPrice) return null;

    try {
      var tiers = JSON.parse(tierDataStr);
      var rules = tiers.price_rules && tiers.price_rules[0] && tiers.price_rules[0].discounts;
      if (!rules || !rules.length) return null;

      var basePrice = variantPrice / 100;
      var tableData = rules.map(function (d) {
        var perUnit = (basePrice - (d.discount || 0)).toFixed(2);
        return { 'Quantity': String(d.lower_limit), 'Per Unit': '$' + perUnit };
      });

      return {
        title: 'Customized Bulk Pricing' + (sku ? ' \u2022 ' + sku : ''),
        desc: '',
        table_data: tableData
      };
    } catch (e) {
      console.warn('[ppo-reorder-to-designer] Could not build discount JSON:', e);
      return null;
    }
  }

  // ── Build designer URL ─────────────────────────────────────────────
  function buildDesignerUrl(pid, vid, qty, dpid, discountJson) {
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    var custID = 0;

    var urlparam = 'customer=' + custID + '&id=' + pid + '&qty=' + qty + '&vid=' + vid + '&pbti=0';
    if (dpid && dpid !== '0' && dpid !== 'undefined') {
      urlparam += '&dpid=' + dpid;
    }
    if (discountJson) {
      urlparam += '&discount=' + encodeURIComponent(JSON.stringify(discountJson));
    }

    var encryptUrl = encrypt(urlparam, secretKeyData);

    if (isMobile || (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2)) {
      return APP_URL + 'mobile/index.html?' + encryptUrl;
    }
    return '/pages/designer-studio?' + encryptUrl;
  }

  // ── Build the reorder banner HTML ──────────────────────────────────
  function createBanner(designerUrl, itemKey, hasArtwork) {
    var banner = document.createElement('div');
    banner.className = BANNER_CLASS;
    banner.style.cssText = [
      'margin: 12px 0 8px;',
      'padding: 16px 18px;',
      'background: linear-gradient(135deg, ' + COLORS.bgStart + ' 0%, ' + COLORS.bgEnd + ' 100%);',
      'border: 2px solid ' + COLORS.border + ';',
      'border-radius: 10px;',
      'text-align: center;'
    ].join('');

    var heading = hasArtwork ? 'Your Design is Saved!' : 'Reorder This Custom Product';
    var message = hasArtwork
      ? 'Open the designer to review your artwork at full size, make any changes (text, colors, etc.), and set your quantity for correct pricing.'
      : 'Open the designer to set your quantity for correct pricing and customize your product. You can upload your artwork or start a new design.';
    var subtext = hasArtwork
      ? 'Your artwork will be loaded automatically'
      : 'Pricing updates based on quantity in the designer';

    banner.innerHTML =
      '<div style="margin-bottom: 10px;">' +
        '<svg style="width:28px; height:28px; color:' + COLORS.primary + '; vertical-align:middle; margin-right:6px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">' +
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>' +
        '</svg>' +
        '<strong style="font-size: 15px; color: ' + COLORS.text + ';">' + heading + '</strong>' +
      '</div>' +
      '<p style="font-size: 13px; color: ' + COLORS.textMed + '; margin: 0 0 6px; line-height: 1.5;">' +
        message +
      '</p>' +
      '<p style="font-size: 12px; color: ' + COLORS.text + '; margin: 0 0 14px; line-height: 1.4; font-weight: 600;">' +
        '⚠ The price shown is from your previous order and may not reflect a revised quantity.' +
      '</p>' +
      '<a href="' + designerUrl + '" ' +
         'class="ppo-reorder-designer-btn" ' +
         'data-item-key="' + (itemKey || '') + '" ' +
         'style="display: inline-block; background: ' + COLORS.primary + '; color: ' + COLORS.white + '; ' +
                'text-decoration: none; font-weight: 700; font-size: 14px; ' +
                'padding: 12px 28px; border-radius: 6px; ' +
                'transition: background 0.2s; cursor: pointer;">' +
        '✏️ Open in Designer' +
      '</a>' +
      '<p style="font-size: 11px; color: ' + COLORS.textMuted + '; margin: 10px 0 0;">' +
        subtext +
      '</p>';

    return banner;
  }

  // ── Dim the stale price on the row ─────────────────────────────────
  function dimStalePrice(row) {
    // PPO's theme uses a different price cell structure
    var priceCell = row.querySelector('td:last-child') ||
                    row.querySelector('.cart-item__total');
    if (priceCell) {
      priceCell.style.opacity = '0.35';
      priceCell.style.position = 'relative';

      var note = document.createElement('div');
      note.style.cssText = 'font-size:11px; color:' + COLORS.primary + '; font-weight:600; margin-top:6px; opacity:1;';
      note.textContent = '⚠ Price updates in designer';
      priceCell.appendChild(note);
    }

    // Also dim inline price in item details
    var priceDivs = row.querySelectorAll('.price__regular, .price__sale');
    priceDivs.forEach(function (el) {
      el.style.opacity = '0.35';
      el.style.textDecoration = 'line-through';
    });
  }

  // ── Add hover/animation styles (once) ──────────────────────────────
  function ensureStyles() {
    if (document.getElementById('ppo-reorder-designer-styles')) return;
    var style = document.createElement('style');
    style.id = 'ppo-reorder-designer-styles';
    style.textContent =
      '.ppo-reorder-designer-btn:hover { background: ' + COLORS.primaryDk + ' !important; }' +
      '.ppo-reorder-designer-banner { animation: ppo-banner-fade-in 0.3s ease-out; }' +
      '@keyframes ppo-banner-fade-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }';
    document.head.appendChild(style);
  }

  // ── Inject banner into a cart row ──────────────────────────────────
  function injectBanner(row, designerUrl, itemKey, hasArtwork) {
    if (row.querySelector('.' + BANNER_CLASS)) return;

    // PPO's Concept theme uses .cart-item__details within the first td
    var detailsCell = row.querySelector('.cart-item__details') ||
                      row.querySelector('td:first-child');
    if (!detailsCell) return;

    var existingEditLink = row.querySelector('a.design-edit-link');
    if (existingEditLink) existingEditLink.style.display = 'none';

    var banner = createBanner(designerUrl, itemKey, hasArtwork);
    detailsCell.appendChild(banner);

    // When clicking "Open in Designer", remove this stale item + any Molds from cart first
    var lineItemKey = row.getAttribute('data-line-item-key');
    if (lineItemKey) {
      var btn = banner.querySelector('.ppo-reorder-designer-btn');
      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var url = btn.getAttribute('href');
          btn.textContent = '⏳ Removing old item…';
          btn.style.pointerEvents = 'none';
          var keysToRemove = getMoldLineItemKeys();
          keysToRemove.unshift(lineItemKey);
          removeLineItems(keysToRemove, function () {
            window.location.href = url;
          });
        });
      }
    }

    dimStalePrice(row);
    ensureStyles();
  }

  // ── Lock quantity controls on phantom rows ─────────────────────────
  function lockQtyControls(row) {
    // Disable qty number inputs
    row.querySelectorAll('input').forEach(function (el) {
      if (el.type === 'number' || el.name === 'quantity' || el.name === 'updates[]') {
        el.disabled = true;
        el.readOnly = true;
        el.style.opacity = '0.4';
        el.style.cursor = 'not-allowed';
      }
    });
    // Disable +/– buttons (leave remove/trash button alone)
    row.querySelectorAll('button').forEach(function (btn) {
      var text = (btn.textContent || '').trim();
      var aria = (btn.getAttribute('aria-label') || '').toLowerCase();
      var isQty = text === '+' || text === '-' || text === '−' || text === '–' ||
                  aria.indexOf('increase') >= 0 || aria.indexOf('decrease') >= 0 ||
                  btn.name === 'plus' || btn.name === 'minus';
      if (isQty) {
        btn.disabled = true;
        btn.style.opacity = '0.3';
        btn.style.pointerEvents = 'none';
      }
    });
    console.log('[ppo-reorder-to-designer] Locked qty controls for phantom row');
  }

  // ── Disable checkout when stale phantom items are in cart ──────────
  function disableCheckoutForPhantoms() {
    function applyDisable() {
      var phantomRows = document.querySelectorAll('tr[data-in-phantom="true"]');
      if (!phantomRows.length) return;

      var mainBtns = document.querySelectorAll(
        'button[name="checkout"], input[name="checkout"], .cart__ctas button[type="submit"]'
      );
      mainBtns.forEach(function (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.style.opacity = '0.45';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
        btn.setAttribute('title', 'Please open your design in the designer first.');
      });

      var dynamicContainers = document.querySelectorAll(
        '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .dynamic-checkout__buttons, [data-shopify="dynamic-checkout-cart"], .shopify-payment-button'
      );
      dynamicContainers.forEach(function (el) {
        el.style.opacity = '0.35';
        el.style.pointerEvents = 'none';
        el.style.filter = 'grayscale(60%)';
      });
    }

    applyDisable();

    var attempts = 0;
    var retry = setInterval(function () {
      applyDisable();
      attempts++;
      if (attempts >= 15) clearInterval(retry);
    }, 400);

    if (window.MutationObserver) {
      var observer = new MutationObserver(function () { applyDisable(); });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 15000);
    }

    // Block checkout clicks and form submits
    document.addEventListener('click', function (e) {
      if (!document.querySelector('tr[data-in-phantom="true"]')) return;
      var target = e.target;
      if (!target || !target.closest) return;
      var checkoutHit = target.closest(
        'button[name="checkout"], input[name="checkout"], .cart__ctas button[type="submit"], ' +
        '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button'
      );
      if (checkoutHit) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var banner = document.querySelector('.' + BANNER_CLASS);
        if (banner) {
          banner.style.transition = 'box-shadow 0.3s';
          banner.style.boxShadow = '0 0 0 4px rgba(103,107,162,0.45)';
          banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () { banner.style.boxShadow = ''; }, 1200);
        }
      }
    }, true);

    document.addEventListener('submit', function (e) {
      if (!document.querySelector('tr[data-in-phantom="true"]')) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.querySelector('button[name="checkout"], input[name="checkout"]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  CASE A: Phantom products from "Buy Again" (no _refid)
  //  Detected via data-in-phantom="true" set by Liquid
  // ═══════════════════════════════════════════════════════════════════
  function handlePhantomProducts() {
    var phantomRows = document.querySelectorAll('tr[data-in-phantom="true"]');

    if (phantomRows.length === 0) {
      console.log('[ppo-reorder-to-designer] No phantom products found');
      return;
    }

    console.log('[ppo-reorder-to-designer] Found', phantomRows.length, 'phantom product(s)');

    // Disable checkout while stale phantom items are present
    disableCheckoutForPhantoms();

    getSecretKey().then(function () {
      phantomRows.forEach(function (row) {
        var phantomVariantId = row.getAttribute('data-phantom-variant');
        if (!phantomVariantId) return;

        // Lock qty controls — customer must use designer for correct pricing
        lockQtyControls(row);

        var parentProductId = row.getAttribute('data-phantom-parent-id');
        var phantomRefId = row.getAttribute('data-phantom-ref-id');

        console.log('[ppo-reorder-to-designer] Phantom data:', {
          phantomVariantId: phantomVariantId,
          parentProductId: parentProductId,
          phantomRefId: phantomRefId
        });

        // Get qty from input
        var qty = 1;
        var qtyInput = row.querySelector('.quantity__input, input[type="number"]');
        if (qtyInput) {
          qty = parseInt(qtyInput.value, 10) || 1;
        }

        // Build discount JSON from tier data on the row
        var discountJson = buildDiscountJson(row);
        if (discountJson) {
          console.log('[ppo-reorder-to-designer] Built discount JSON with', discountJson.table_data.length, 'tiers');
        }

        if (parentProductId) {
          resolvePhantomVariant(phantomVariantId)
            .then(function (ids) {
              var pid = parentProductId || ids.pid;
              var vid = ids.vid;
              var dpid = phantomRefId || null;
              var designerUrl = buildDesignerUrl(pid, vid, qty, dpid, discountJson);
              console.log('[ppo-reorder-to-designer] Designer URL:', designerUrl);
              injectBanner(row, designerUrl, '', !!phantomRefId);
            })
            .catch(function (err) {
              console.warn('[ppo-reorder-to-designer] API fallback failed:', err);
              var designerUrl = buildDesignerUrl(parentProductId, '', qty, phantomRefId || null, discountJson);
              injectBanner(row, designerUrl, '', !!phantomRefId);
            });
        } else {
          resolvePhantomVariant(phantomVariantId)
            .then(function (ids) {
              var designerUrl = buildDesignerUrl(ids.pid, ids.vid, qty, phantomRefId || null, discountJson);
              console.log('[ppo-reorder-to-designer] Designer URL (API):', designerUrl);
              injectBanner(row, designerUrl, '', !!phantomRefId);
            })
            .catch(function (err) {
              console.warn('[ppo-reorder-to-designer] Could not resolve phantom', phantomVariantId, err);
            });
        }
      });
    }).catch(function (err) {
      console.warn('[ppo-reorder-to-designer] Could not load secret key:', err);
    });
  }

  // ── Init ───────────────────────────────────────────────────────────
  setTimeout(function () {
    handlePhantomProducts();
  }, 500);

}());
